import "./AdminDirectionsPage.scss";
import "../Home/HomePage.scss";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header/Header";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { AdminContentApi } from "../../services/api/adminContent";
import { DirectionResponse, DirectionUpsertRequest } from "../../types/types";
import { isAdminRole } from "../../utils/roles";
import { getErrorMessage } from "../../utils/errorMessage";

const EMPTY_FORM: DirectionUpsertRequest = { name: "" };

export const AdminDirectionsPage = () => {
  const navigate = useNavigate();
  const { user } = useCurrentUser();

  const [directions, setDirections] = useState<DirectionResponse[]>([]);
  const [createForm, setCreateForm] = useState<DirectionUpsertRequest>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingForm, setEditingForm] = useState<DirectionUpsertRequest>(EMPTY_FORM);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canAccess = useMemo(() => isAdminRole(user?.role), [user?.role]);

  const fetchDirections = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await AdminContentApi.getDirections();
      setDirections(response);
    } catch (requestError) {
      const message = getErrorMessage(requestError, "Не удалось загрузить направления. Попробуйте еще раз.");
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canAccess) {
      return;
    }

    void fetchDirections();
  }, [canAccess, fetchDirections]);

  const resetCreateForm = useCallback(() => setCreateForm(EMPTY_FORM), []);

  const closeCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
    resetCreateForm();
  }, [resetCreateForm]);

  const handleCreate = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setSaving(true);
      setError(null);

      try {
        const created = await AdminContentApi.createDirection(createForm);
        setDirections((current) => [...current, created]);
        closeCreateModal();
      } catch (requestError) {
        const message = getErrorMessage(requestError, "Не удалось создать направление.");
        setError(message);
      } finally {
        setSaving(false);
      }
    },
    [closeCreateModal, createForm]
  );

  const startEdit = useCallback((item: DirectionResponse) => {
    setEditingId(item.id);
    setEditingForm({ name: item.name });
    setError(null);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditingForm(EMPTY_FORM);
  }, []);

  const handleUpdate = useCallback(
    async (id: number) => {
      setSaving(true);
      setError(null);

      try {
        const updated = await AdminContentApi.updateDirection(id, editingForm);
        setDirections((current) => current.map((item) => (item.id === id ? updated : item)));
        cancelEdit();
      } catch (requestError) {
        const message = getErrorMessage(requestError, "Не удалось обновить направление.");
        setError(message);
      } finally {
        setSaving(false);
      }
    },
    [cancelEdit, editingForm]
  );

  const handleDelete = useCallback(async (id: number) => {
    if (!window.confirm("Вы уверены, что хотите удалить это направление?")) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await AdminContentApi.deleteDirection(id);
      setDirections((current) => current.filter((item) => item.id !== id));
    } catch (requestError) {
      const message = getErrorMessage(requestError, "Не удалось удалить направление.");
      setError(message);
    } finally {
      setSaving(false);
    }
  }, []);

  if (!canAccess) {
    return (
      <>
        <Header />
        <main className="admin-directions">
          <section className="admin-directions__card">
            <p className="admin-directions__empty">Доступ к этому разделу есть только у администратора.</p>
          </section>
        </main>
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="admin-directions">
        <section className="admin-directions__hero">
          <div>
            <button className="admin-directions__back" type="button" onClick={() => navigate("/app/admin")}>
              ← Назад
            </button>
            <p className="admin-directions__eyebrow">Админка</p>
            <h1 className="admin-directions__title">Направления</h1>
            <p className="admin-directions__subtitle">
              Управление направлениями вопросов: создание, редактирование и удаление.
            </p>
          </div>
          <aside className="admin-directions__summary">
            <div>
              <strong>Всего:</strong> {directions.length}
            </div>
          </aside>
        </section>

        <section className="admin-directions__card">
          <div className="admin-directions__toolbar">
            <button
              className="admin-directions__button admin-directions__buttonPrimary"
              type="button"
              onClick={() => setIsCreateModalOpen(true)}
            >
              Добавить направление
            </button>
          </div>
          {error ? <p className="admin-directions__error">{error}</p> : null}
        </section>

        <section className="admin-directions__card admin-directions__card_table">
          <div className="admin-directions__tableWrap">
            <table className="admin-directions__table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Название</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {directions.length === 0 ? (
                  <tr>
                    <td colSpan={3}>
                      <p className="admin-directions__empty">{loading ? "Загрузка..." : "Направления не найдены."}</p>
                    </td>
                  </tr>
                ) : (
                  directions.map((item) => {
                    const isEditing = editingId === item.id;
                    return (
                      <tr key={item.id}>
                        <td>{item.id}</td>
                        <td>
                          {isEditing ? (
                            <input
                              className="admin-directions__input"
                              value={editingForm.name}
                              onChange={(event) => setEditingForm((current) => ({ ...current, name: event.target.value }))}
                            />
                          ) : item.name}
                        </td>
                        <td>
                          <div className="admin-directions__actions">
                            {isEditing ? (
                              <>
                                <button
                                  className="admin-directions__button admin-directions__buttonPrimary"
                                  type="button"
                                  onClick={() => void handleUpdate(item.id)}
                                  disabled={saving}
                                >
                                  Сохранить
                                </button>
                                <button className="admin-directions__button" type="button" onClick={cancelEdit} disabled={saving}>
                                  Отмена
                                </button>
                              </>
                            ) : (
                              <>
                                <button className="admin-directions__button" type="button" onClick={() => startEdit(item)} disabled={saving}>
                                  Изменить
                                </button>
                                <button
                                  className="admin-directions__button admin-directions__buttonDanger"
                                  type="button"
                                  onClick={() => void handleDelete(item.id)}
                                  disabled={saving}
                                >
                                  Удалить
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      {isCreateModalOpen ? (
        <section className="home-modal">
          <div className="home-modal__backdrop" onClick={closeCreateModal} />
          <div className="home-modal__dialog">
            <button className="home-modal__close" type="button" onClick={closeCreateModal}>x</button>
            <h2 className="home-modal__title">Новое направление</h2>
            <form className="home-modal__form" onSubmit={handleCreate}>
              <input
                className="home-modal__input"
                type="text"
                placeholder="Название направления"
                value={createForm.name}
                onChange={(event) => setCreateForm((current) => ({ ...current, name: event.target.value }))}
              />
              <button className="home-modal__submit" type="submit" disabled={saving}>
                {saving ? "Сохранение..." : "Создать"}
              </button>
            </form>
          </div>
        </section>
      ) : null}
    </>
  );
};
